
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { AppTagManagement } from "./AppTagManagement";

interface App {
  id: string;
  bundle_id: string;
  app_name: string;
  app_icon_url?: string;
  version_code?: string;
  category?: string;
  tags: string[];
  tag_ids?: string[];
  description?: string;
  created_at: string;
  updated_at: string;
}

interface AppCategoryTagsCompactProps {
  app: App;
  onTagsUpdate: (newTagIds: string[]) => void;
  onCategoryUpdate: (newCategory: string) => void;
}

export const AppCategoryTagsCompact = ({ app, onTagsUpdate, onCategoryUpdate }: AppCategoryTagsCompactProps) => {
  const { toast } = useToast();

  const categoriesData = useQuery(api.appCategories.list.categories);
  const updateAppCategory = useMutation(api.apps.update.category);

  // Transform Convex categories data
  const categories = categoriesData ? 
    (Array.isArray(categoriesData) ? categoriesData : [])
      .map((category: any, index: number) => ({
        _id: category._id || category.id || `category-${index}`,
        name: category.name || "",
      }))
      .filter((category) => category.name) : [];

  const handleCategoryChange = async (newCategory: string) => {
    try {
      // Convert "none" back to null for database storage
      const categoryValue = newCategory === "none" ? null : newCategory;
      
      await updateAppCategory({
        app: app.id as any, // The Convex API expects an Id<"apps"> type
        category: categoryValue || "",
      });
      
      // Update local state immediately
      onCategoryUpdate(categoryValue || "");
      
      // Clear tags when category changes as tags are category-specific
      onTagsUpdate([]);
      
      toast({
        title: "Success",
        description: "App category updated successfully",
      });
    } catch (error) {
      console.error('Error updating app category:', error);
      toast({
        title: "Error",
        description: "Failed to update app category",
        variant: "destructive",
      });
    }
  };

  // Convert null/empty category to "none" for the select component
  const selectValue = app.category || "none";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Category</label>
          <Select 
            value={selectValue} 
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No category</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category._id} value={category.name}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Tags</label>
          <AppTagManagement
            appId={app.id}
            categoryName={app.category}
            currentTagIds={app.tag_ids || []}
            onTagsUpdate={onTagsUpdate}
          />
        </div>
      </div>
    </div>
  );
};
